import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { Button, colors, Divider, Row, useInfoSheet, H2, LayoutBand, Separator } from '@fs/zion-ui'
import { ArrowCaret, ControlSettingsAdjust } from '@fs/zion-icon'
import { useParams, useLocation } from '@fs/zion-router'
import { Title } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import axios, { useZionAxios } from '@fs/zion-axios'
import { usePageView } from '@fs/zion-analytics'
import useGroup from '../services/useGroup'
import HiddenTreeMessage from '../components/details/researcher-trees/HiddenTreeMessage'
import useTree from '../services/useTree'
import CenteredLoadingSpinner from '../components/CenteredLoadingSpinner'
import Table from '../components/details/researcher-trees/contributions/Table'
import OptionsSideSheet from '../components/details/researcher-trees/contributions/OptionsSideSheet'

// queryParamsObj:
// oldestToNewest?: boolean
// cisId?: string
// timestamp?: string
const contributionReducer = (state, { type, contributionList, persons, nextPageToken, error, queryParamsObj }) => {
  switch (type) {
    case 'INIT': {
      return { ...state, contributionList, persons, nextPageToken }
    }
    case 'CHANGE_OPTIONS': {
      const newQueryParamsObj = Object.fromEntries(
        Object.entries({ ...state.queryParamsObj, ...queryParamsObj }).filter(([name, value]) => {
          if ((name === 'timestamp' && value === 0) || value === undefined) {
            return false
          }
          return true
        })
      )
      return { queryParamsObj: newQueryParamsObj }
    }
    case 'START_UPDATE': {
      return { ...state, isLoadingNextPage: true }
    }
    case 'COMPLETE_UPDATE': {
      return {
        ...state,
        contributionList: [...state.contributionList, ...contributionList],
        persons: { ...state.persons, ...persons },
        nextPageToken,
        isLoadingNextPage: false,
      }
    }
    case 'ERROR': {
      return { ...state, error, nextPageToken, isLoadingNextPage: false }
    }
    default: {
      throw new Error('Wrong type used for contributionReducer')
    }
  }
}

const getContributions = (treeId, queryParamsObj) => {
  const params = { ...queryParamsObj }
  // the backend says we can't send timestamp and from together - TF-3601
  if (queryParamsObj.from) {
    delete params.timestamp
  }
  // If timeStamp is valid then oldestToNewest needs to be true
  if (params.timestamp) {
    params.oldestToNewest = true
  }
  const queryParams = new URLSearchParams(params)
  return axios.get(
    `/service/tree/tree-data/user/all/tree/${treeId}/contributions${queryParams.size ? `?${queryParams}` : ''}`
  )
}

const initialState = {
  queryParamsObj: {
    oldestToNewest: false,
  },
}

const transformResponse = (res) => JSON.parse(res)?.contributors

export default function TreeChangesPage() {
  usePageView({ page_detail: 'ReT: Tree Changes' })
  const [t] = useTranslation()
  const { groupId } = useParams()
  const { state } = useLocation()
  const optionsInfoSheet = useInfoSheet()
  const [contributionsState, contributionsDispatch] = useReducer(contributionReducer, initialState)
  const isCurrent = useRef()
  const group = state?.group
  const { data: groupDetails } = useGroup(group?.id ? null : groupId)
  const selectedGroup = group ?? groupDetails
  const treeId = selectedGroup?.treeIds?.[0]
  const { data: treeDetails, loading: treeLoading } = useTree(treeId)
  const loading = (!group && !groupDetails) || treeLoading
  const isHidden = treeDetails?.hidden

  // This is a deliberate choice to not use the historical persons for the tree changelog.
  const historicalPersonsIsOn = false

  const [{ data: contributors }] = useZionAxios({
    url: treeId && !isHidden ? `/service/tree/tree-data/user/all/tree/${treeId}/contributors` : undefined,
    transformResponse,
  })

  useEffect(() => {
    isCurrent.current = true
    return () => {
      isCurrent.current = false
    }
  })

  const queryParamsObj = contributionsState.queryParamsObj
  useEffect(() => {
    if (treeId && !isHidden) {
      getContributions(treeId, { ...queryParamsObj, useHistoryPersons: historicalPersonsIsOn })
        .then(({ data }) => {
          if (isCurrent.current) {
            contributionsDispatch({
              type: 'INIT',
              contributionList: data.contributions,
              persons: data.persons,
              nextPageToken: data.nextPageToken,
            })
          }
          return undefined
        })
        .catch((err) => {
          if (isCurrent.current) {
            console.error(err)
            contributionsDispatch({ type: 'ERROR', error: err })
          }
        })
    }
  }, [isHidden, queryParamsObj, treeId, historicalPersonsIsOn])

  const loadNextPage = useCallback(() => {
    if (!contributionsState.isLoadingNextPage && contributionsState?.nextPageToken) {
      contributionsDispatch({ type: 'START_UPDATE' })
      getContributions(treeId, {
        ...queryParamsObj,
        from: contributionsState.nextPageToken,
        useHistoryPersons: historicalPersonsIsOn,
      })
        .then(({ data }) => {
          if (isCurrent.current) {
            contributionsDispatch({
              type: 'COMPLETE_UPDATE',
              contributionList: data.contributions,
              persons: data.persons,
              nextPageToken: data.nextPageToken,
            })
          }
          return undefined
        })
        .catch((err) => {
          if (isCurrent.current) {
            console.error(err)
            contributionsDispatch({
              type: 'ERROR',
              error: err,
            })
          }
        })
    }
  }, [
    contributionsState.isLoadingNextPage,
    contributionsState.nextPageToken,
    queryParamsObj,
    treeId,
    historicalPersonsIsOn,
  ])

  if (loading) {
    return <CenteredLoadingSpinner />
  }

  if (isHidden) {
    return (
      <>
        <Separator />
        <HiddenTreeMessage />
      </>
    )
  }

  const changeOptions = (paramsObj) => {
    contributionsDispatch({ type: 'CHANGE_OPTIONS', queryParamsObj: paramsObj })
  }
  const handleSort = (columnId, asc) => {
    if (columnId === 'column3') {
      setTimeout(() => {
        contributionsDispatch({ type: 'CHANGE_OPTIONS', queryParamsObj: { oldestToNewest: asc } })
      }, 50)
    }
  }
  return (
    <>
      <Title>{t('group-management.latest-changes-page.title', { groupName: selectedGroup.name })}</Title>
      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        <Button
          data-testid="back-to-group-button"
          Icon={ArrowCaret}
          iconDirection="backward"
          to={`/research-tree/${selectedGroup.id}/details`}
        >
          {t('group-management.back-to-group.action', { groupName: selectedGroup.name })}
        </Button>
      </LayoutBand>

      <Divider fullWidth />

      <LayoutBand maxWidth="xxl" color={colors.gray00} marginY="nano">
        <Row>
          <H2 growX size="H5">
            {t('group-management.latest-changes.heading')}
          </H2>
          <Button
            data-testid="tree-changes-options-button"
            onClick={optionsInfoSheet.handleClick}
            emphasis="medium"
            Icon={ControlSettingsAdjust}
            linkName="tree changes options button"
          >
            {t('group-management.options.action')}
          </Button>
        </Row>

        <Separator size="nano" />

        <Table
          contributionList={contributionsState.contributionList}
          loading={!contributionsState.contributionList && !contributionsState.error}
          error={contributionsState.error}
          contributionsPersons={contributionsState.persons}
          isLastPage={!contributionsState.nextPageToken}
          loadNextPage={loadNextPage}
          oldestToNewest={contributionsState?.queryParamsObj?.oldestToNewest}
          onSort={handleSort}
          removeSort={contributionsState.queryParamsObj.timestamp}
        />
      </LayoutBand>
      <OptionsSideSheet
        infoSheetConfig={optionsInfoSheet}
        contributors={contributors}
        changeOptions={changeOptions}
        oldestToNewest={contributionsState.queryParamsObj.oldestToNewest}
      />
    </>
  )
}
